import type { ReactNode } from 'react';
import {
  DeliveredProcedureOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  GiftOutlined,
  IdcardOutlined,
  PartitionOutlined,
  SafetyOutlined,
  SettingOutlined,
  ShopOutlined,
  TagOutlined,
  TagsOutlined,
  TeamOutlined,
  BellOutlined,
} from '@ant-design/icons';
import type { Business } from '../../../types/business';
import { isFeatureEnabled } from '../../../types/business';
import type { RolePermissions } from '../../../types/user';
import i18n from '../../../i18n';

export type SettingsNavItem = {
  key: string;
  path: string;
  label: string;
  description: string;
  icon: ReactNode;
};

type SettingsNavContext = {
  permissions: RolePermissions | null | undefined;
  business: Business | null | undefined;
};

function navLabel(key: string, field: 'label' | 'description'): string {
  return String(i18n.t(`nav.${key}.${field}`, { ns: 'settings' }));
}

/** Entrées du hub Paramètres (config rare), filtrées par droits + features. */
export function getSettingsNavItems({
  permissions,
  business,
}: SettingsNavContext): SettingsNavItem[] {
  const items: SettingsNavItem[] = [];

  // Prefs perso — toujours accessible (pas de permission métier)
  items.push({
    key: 'notifications',
    path: '/settings/notifications',
    label: navLabel('notifications', 'label'),
    description: navLabel('notifications', 'description'),
    icon: <BellOutlined />,
  });

  if (permissions?.canViewInvoicing && isFeatureEnabled(business ?? null, 'pos')) {
    items.push({
      key: 'invoicing',
      path: '/settings/invoicing',
      label: navLabel('invoicing', 'label'),
      description: navLabel('invoicing', 'description'),
      icon: <FileTextOutlined />,
    });
  }

  if (
    permissions?.canViewLoyaltyPass &&
    isFeatureEnabled(business ?? null, 'loyaltyPass')
  ) {
    items.push({
      key: 'loyalty-pass',
      path: '/settings/loyalty-pass',
      label: navLabel('loyaltyPass', 'label'),
      description: navLabel('loyaltyPass', 'description'),
      icon: <IdcardOutlined />,
    });
  }

  if (
    permissions?.canViewCatalogPacks &&
    isFeatureEnabled(business ?? null, 'catalogPacks')
  ) {
    items.push({
      key: 'catalog-packs',
      path: '/settings/packs',
      label: navLabel('catalogPacks', 'label'),
      description: navLabel('catalogPacks', 'description'),
      icon: <GiftOutlined />,
    });
  }

  if (permissions?.canViewRoleProfiles && isFeatureEnabled(business ?? null, 'employeesPayroll')) {
    items.push({
      key: 'role-profiles',
      path: '/settings/role-profiles',
      label: navLabel('roleProfiles', 'label'),
      description: navLabel('roleProfiles', 'description'),
      icon: <SafetyOutlined />,
    });
  }

  if (permissions?.canViewPipelines && isFeatureEnabled(business ?? null, 'pipelineConfig')) {
    items.push({
      key: 'pipeline',
      path: '/settings/pipeline',
      label: navLabel('pipeline', 'label'),
      description: navLabel('pipeline', 'description'),
      icon: <PartitionOutlined />,
    });
  }

  if (permissions?.canViewClientTypes && isFeatureEnabled(business ?? null, 'clientTypes')) {
    items.push({
      key: 'client-types',
      path: '/activity/client-types',
      label: navLabel('clientTypes', 'label'),
      description: navLabel('clientTypes', 'description'),
      icon: <TeamOutlined />,
    });
  }

  if (
    permissions?.canViewOpportunityCategories &&
    isFeatureEnabled(business ?? null, 'opportunityCategories')
  ) {
    items.push({
      key: 'opportunity-categories',
      path: '/activity/opportunity-categories',
      label: navLabel('opportunityCategories', 'label'),
      description: navLabel('opportunityCategories', 'description'),
      icon: <TagOutlined />,
    });
  }

  if (permissions?.canViewActivityTypes && isFeatureEnabled(business ?? null, 'activityTypes')) {
    items.push({
      key: 'activity-types',
      path: '/activity/activity-types',
      label: navLabel('activityTypes', 'label'),
      description: navLabel('activityTypes', 'description'),
      icon: <FileTextOutlined />,
    });
  }

  if (permissions?.canViewCategories && isFeatureEnabled(business ?? null, 'inventory')) {
    items.push({
      key: 'product-categories',
      path: '/inventory/categories',
      label: navLabel('productCategories', 'label'),
      description: navLabel('productCategories', 'description'),
      icon: <TagOutlined />,
    });
  }

  if (permissions?.canViewProducts && isFeatureEnabled(business ?? null, 'inventory')) {
    items.push({
      key: 'packaging-units',
      path: '/inventory/packaging-units',
      label: navLabel('packagingUnits', 'label'),
      description: navLabel('packagingUnits', 'description'),
      icon: <ExperimentOutlined />,
    });
  }

  if (
    permissions?.canViewExternalDestinations &&
    isFeatureEnabled(business ?? null, 'inventoryExternalDispatch')
  ) {
    items.push({
      key: 'external-destinations',
      path: '/inventory/external-destinations',
      label: navLabel('externalDestinations', 'label'),
      description: navLabel('externalDestinations', 'description'),
      icon: <ShopOutlined />,
    });
  }

  if (permissions?.canViewServices && isFeatureEnabled(business ?? null, 'services')) {
    items.push({
      key: 'service-categories',
      path: '/services/categories',
      label: navLabel('serviceCategories', 'label'),
      description: navLabel('serviceCategories', 'description'),
      icon: <SettingOutlined />,
    });
  }

  if (
    permissions?.canManagePickups &&
    isFeatureEnabled(business ?? null, 'pickup')
  ) {
    items.push({
      key: 'pickup',
      path: '/pickups/settings',
      label: navLabel('pickup', 'label'),
      description: navLabel('pickup', 'description'),
      icon: <DeliveredProcedureOutlined />,
    });
  }

  if (
    permissions?.canViewExpenseCategories &&
    isFeatureEnabled(business ?? null, 'expenseCategories')
  ) {
    items.push({
      key: 'expense-categories',
      path: '/expenses/categories',
      label: navLabel('expenseCategories', 'label'),
      description: navLabel('expenseCategories', 'description'),
      icon: <TagsOutlined />,
    });
  }

  return items;
}

export function hasAnySettingsAccess(ctx: SettingsNavContext): boolean {
  return getSettingsNavItems(ctx).length > 0;
}
